import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtDate } from "./formatters";

interface DecisionRow {
  id: string;
  code: string;
  title: string;
  category: string | null;
  status: string;
  submittedByName: string | null;
  decidedByName: string | null;
  submittedAt: string;
  decidedAt: string | null;
  daysToDecision: number | null;
  linkedIdeasCount: number;
  projectName: string | null;
}

interface DecisionsData {
  data: DecisionRow[];
  summary: {
    total: number;
    decided: number;
    implemented: number;
    pending: number;
    avgDaysToDecision: number;
  };
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    open: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Open" },
    decided: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Decided" },
    implemented: {
      bg: "hsl(142,55%,92%)",
      fg: "hsl(142,72%,28%)",
      label: "Implemented",
    },
    archived: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Archived" },
  };
  const s = map[status] ?? map.open;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

export function DecisionsLogReport({ data }: { data: DecisionsData }) {
  const cols: Array<TableColumn<DecisionRow>> = [
    {
      key: "code",
      label: "#",
      width: 110,
      render: (r) => (
        <span className="font-mono text-[12px] text-muted-foreground">{r.code}</span>
      ),
    },
    {
      key: "title",
      label: "Title",
      render: (r) => <span className="font-medium text-foreground">{r.title}</span>,
    },
    {
      key: "category",
      label: "Category",
      render: (r) => r.category ?? "—",
    },
    {
      key: "status",
      label: "Status",
      render: (r) => <StatusBadge status={r.status} />,
    },
    { key: "submittedByName", label: "Submitter", render: (r) => r.submittedByName ?? "—" },
    { key: "decidedByName", label: "Decided By", render: (r) => r.decidedByName ?? "—" },
    {
      key: "submittedAt",
      label: "Submitted",
      render: (r) => fmtDate(r.submittedAt),
    },
    {
      key: "decidedAt",
      label: "Decided",
      render: (r) => fmtDate(r.decidedAt),
    },
    {
      key: "daysToDecision",
      label: "Days",
      align: "right",
      render: (r) => (r.daysToDecision !== null ? `${r.daysToDecision}d` : "—"),
    },
    { key: "projectName", label: "Project", render: (r) => r.projectName ?? "—" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total" value={data.summary.total} />
        <StatCard label="Decided" value={data.summary.decided} tone="good" />
        <StatCard
          label="Implemented"
          value={data.summary.implemented}
          tone="good"
        />
        <StatCard label="Pending" value={data.summary.pending} tone="warn" />
        <StatCard
          label="Avg Days to Decision"
          value={`${data.summary.avgDaysToDecision}d`}
        />
      </div>

      <ReportSection title="Decisions Log">
        <ReportTable columns={cols} rows={data.data} rowKey={(r) => r.id} />
      </ReportSection>
    </div>
  );
}
